import React, { useState, useEffect } from "react";
import useHttpRequests from "../../../hooks/useHttpRequests";
import { TableWithStickyHeader } from "../../uiControls/tableWithStickyHeader";
import { FacilityAdd } from "./facilityAdd";
import { FacilityUpdate } from "./facilityUpdate";
import { AddNew } from "../../uiControls/addNew";
import * as utils from "../../common/utils";
import { ConfirmationDialog } from "../../common/confirmationDialog";

const facilityColumns = [
  {
    label: "Name",
    keyField: "facilityKey",
    dataField: "name",
  },
  {
    label: "Support Email",
    keyField: "facilityKey",
    dataField: "supportEmail",
  },
  {
    label: "Status",
    keyField: "facilityKey",
    dataField: "active",
    minWidth: 20,
    maxWidth: 20,
  },
  {
    label: "",
    keyField: "facilityKey",
    dataField: "facilityKey",
    minWidth: 20,
    maxWidth: 20,
  },
];

const facilitySearchColumns = [
  "facilityName",
  "name",
  "description",
  "supportEmail",
];

export const FacilitySettings = () => {
  const [updateOpen, setUpdateOpen] = useState(false);
  const [updateOpenType, setUpdateOpenType] = useState("");
  const [functionalAreas, setFunctionalAreas] = useState([]);
  const [datasources, setDatasources] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [facilityAttributes, setFacilityAttributes] = useState({
    facilityKey: 0,
    name: "",
    description: "",
    houseCode: "",
    supportEmail: "",
    functionalAreaKey: 0,
    functionalAreaTitle: "",
    datasourceKey: 0,
    datasourceTitle: "",
    timezoneKey: 0,
    timezoneTitle: "",
    active: true,
    isTestFacility: false,
    appUnit: 0,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmationDialogAttributes, setConfirmationDialogAttributes] =
    useState({});

  const { sendRequest: fetchFunctionAreas } = useHttpRequests();
  const { sendRequest: fetchDataSources } = useHttpRequests();
  const { sendRequest: fetchTimeZones } = useHttpRequests();
  const { sendRequest: fetchFacilityDetails } = useHttpRequests();

  const facilityEditHandler = (e, key) => {
    e.stopPropagation();
    if (updateOpen) {
      return false;
    }

    const getFacilityDetails = (facilityObj) => {
      if (!facilityObj || !facilityObj.isSuccess || facilityObj.errorResponse) {
        console.log("Facility fetch response returns error");
        return;
      }

      setFacilityAttributes({
        facilityKey: facilityObj["facility"]["facilityKey"],
        facilityBrief: facilityObj["facility"]["name"],
        name: facilityObj["facility"]["description"],
        houseCode: facilityObj["facility"]["houseCode"],
        supportEmail: facilityObj["facility"]["supportEmail"],
        functionalAreaKey: facilityObj["facility"]["functionalAreaKey"],
        datasourceKey: facilityObj["facility"]["datasourceKey"],
        timezoneKey: facilityObj["facility"]["timezoneKey"],
        appUnit: facilityObj["facility"]["appUnit"],
        isTestFacility: facilityObj["facility"]["isTestFacility"] ?? false,
        functionalAreaTitle: utils.findStringById(
          functionalAreas,
          "id",
          facilityObj["facility"]["functionalAreaKey"]
        )?.["label"],
        datasourceTitle: utils.findStringById(
          datasources,
          "id",
          facilityObj["facility"]["datasourceKey"]
        )?.["label"],
        timezoneTitle: utils.findStringById(
          timezones,
          "id",
          facilityObj["facility"]["timezoneKey"]
        )?.["label"],
        active: facilityObj["facility"]["active"],
      });

      setUpdateOpen(true);
      setUpdateOpenType("edit");
    };

    fetchFacilityDetails(
      { url: "Facilities/" + key, method: "GET", header: true },
      getFacilityDetails
    );
  };

  const changeFacilityStatus = (e, key, status, name) => {
    e.stopPropagation();
    setConfirmationDialogAttributes({
      title: "Facility status change confirmation!",
      message:
        "Are you sure to mark " +
        (status ? "active" : "inactive") +
        " the " +
        name +
        "?",
      confirmText: "Yes",
      cancelText: "No",
      url: "Facilities/",
      keyField: "facilityKey",
      key: key,
      status: status,
      messageTitle: name,
    });
    setDialogOpen(true);
  };

  const getUrl = (params) => {
    return "Facilities?" + params + "&isAdd=false";
  };

  useEffect(() => {
    const getFunctionalAreasResponse = (obj) => {
      if (!obj || !obj.isSuccess || obj.errorResponse) {
        console.log("Functional area fetch response returns error");
        return;
      }
      setFunctionalAreas(
        obj["functionalAreas"]?.map((t) => {
          return { id: t["functionalAreaKey"], label: t["name"] };
        })
      );
    };

    fetchFunctionAreas(
      { url: "FunctionalAreas/Active", method: "GET", header: true },
      getFunctionalAreasResponse
    );
  }, []);

  useEffect(() => {
    const getDataSourcesResponse = (obj) => {
      if (!obj || !obj.isSuccess || obj.errorResponse) {
        console.log("Data sources fetch response returns error");
        return;
      }
      setDatasources(
        obj["datasources"]?.map((t) => {
          return { id: t["datasourceKey"], label: t["name"] };
        })
      );
    };

    fetchDataSources(
      { url: "DataSources/Active", method: "GET", header: true },
      getDataSourcesResponse
    );
  }, []);

  useEffect(() => {
    const getTimeZonesResponse = (obj) => {
      if (!obj || !obj.isSuccess || obj.errorResponse) {
        console.log("Time zones fetch response returns error");
        return;
      }
      setTimezones(
        obj["timeZones"]?.map((t) => {
          return { id: t["timezoneKey"], label: t["name"] };
        })
      );
    };

    fetchTimeZones(
      { url: "TimeZones/Active", method: "GET", header: true },
      getTimeZonesResponse
    );
  }, []);

  const facilityDetailProps = {
    setUpdateOpen,
    facilityAttributes,
    functionalAreas,
    datasources,
    timezones,
  };

  return (
    <div
      style={{
        display: "flex",
        flexFlow: "column",
        height: "calc(100vh - 160px)",
      }}
    >
      <ConfirmationDialog
        open={dialogOpen}
        setDialogOpen={setDialogOpen}
        confirmationDialogAttributes={confirmationDialogAttributes}
      />
      <div
        style={{
          display: "flex",
          flexFlow: "column",
          gap: "20px",
          width: "100%",
        }}
      >
        {!updateOpen && (
          <>
            <TableWithStickyHeader
              title=""
              pagging={true}
              columns={facilityColumns}
              url={getUrl}
              sourceObj="facilities"
              editHandler={facilityEditHandler}
              updateOpen={updateOpen || dialogOpen}
              searchOnColumns={facilitySearchColumns}
              changeStatus={changeFacilityStatus}
              searchPlace="right"
            />
            <AddNew
              onClickCall={() => {
                setFacilityAttributes({
                  facilityKey: 0,
                  name: "",
                  description: "",
                  houseCode: "",
                  supportEmail: "",
                  functionalAreaKey: 0,
                  functionalAreaTitle: "",
                  datasourceKey: 0,
                  datasourceTitle: "",
                  timezoneKey: 0,
                  timezoneTitle: "",
                  active: true,
                  isTestFacility: false,
                });
                setUpdateOpen(true);
                setUpdateOpenType("add");
              }}
            />
          </>
        )}
      </div>

      {updateOpen && updateOpenType === "add" && (
        <div style={{ display: "flex", width: "100%" }}>
          <FacilityAdd {...facilityDetailProps} />
        </div>
      )}
      {updateOpen && updateOpenType === "edit" && (
        <div
          style={{
            display: "flex",
            width: window.innerWidth <= 1366 ? "100%" : "65%",
          }}
        >
          {<FacilityUpdate {...facilityDetailProps} />}
        </div>
      )}
    </div>
  );
};
